import { useEffect, useState } from "react";
import { Button, Empty, Form, Input, Modal, Popconfirm, Table, Typography, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { api } from "@/services/api";

const { Title } = Typography;

interface UserMcp {
  id: string;
  name: string;
  url: string;
  headers: Record<string, string>;
  allowed_tools: string[];
  created_at: string;
}

const mcpApi = {
  list: () => api.get<UserMcp[]>("/my-mcps"),
  create: (data: any) => api.post<UserMcp>("/my-mcps", data),
  update: (id: string, data: any) => api.put<UserMcp>(`/my-mcps/${id}`, data),
  delete: (id: string) => api.delete<void>(`/my-mcps/${id}`),
};

export function UserMcpPage() {
  const { t } = useTranslation();
  const [mcps, setMcps] = useState<UserMcp[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UserMcp | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setMcps(await mcpApi.list()); }
    catch { setMcps([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (r: UserMcp) => {
    setEditing(r);
    const headers = r.headers && Object.keys(r.headers).length > 0
      ? JSON.stringify(r.headers, null, 2)
      : "";
    form.setFieldsValue({
      name: r.name, url: r.url,
      headers_json: headers,
      allowed_tools: (r.allowed_tools || []).join(", "),
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    let headers: Record<string, string> = {};
    if (values.headers_json && values.headers_json.trim()) {
      try {
        headers = JSON.parse(values.headers_json);
      } catch {
        message.error(t("my_mcps.form.headers_json_invalid"));
        return;
      }
    }
    const payload = {
      name: values.name, url: values.url, headers,
      allowed_tools: values.allowed_tools ? values.allowed_tools.split(",").map((s: string) => s.trim()).filter(Boolean) : [],
    };
    setSaving(true);
    try {
      if (editing) { await mcpApi.update(editing.id, payload); }
      else { await mcpApi.create(payload); }
      message.success(t("success"));
      setModalOpen(false);
      load();
    } catch (e) { message.error(e instanceof Error ? e.message : t("error")); }
    finally { setSaving(false); }
  };

  const columns = [
    { title: t("my_mcps.table.name"), dataIndex: "name", key: "name" },
    { title: t("my_mcps.table.url"), dataIndex: "url", key: "url", ellipsis: true },
    {
      title: t("action"), key: "action", width: 80,
      render: (_: unknown, r: UserMcp) => (
        <span style={{ display: "flex", gap: 4 }}>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEdit(r)} />
          <Popconfirm title={t("my_mcps.delete.confirm")} onConfirm={async () => { await mcpApi.delete(r.id); load(); }}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "24px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>{t("my_mcps.title")}</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
          setEditing(null); form.resetFields(); setModalOpen(true);
        }}>{t("my_mcps.create")}</Button>
      </div>
      {mcps.length === 0 && !loading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Empty description={t("my_mcps.empty")} />
        </div>
      ) : (
        <Table dataSource={mcps} columns={columns} rowKey="id" loading={loading} size="middle" />
      )}
      <Modal
        title={editing ? t("edit") : t("my_mcps.create")}
        open={modalOpen} onOk={handleSubmit} onCancel={() => setModalOpen(false)}
        confirmLoading={saving} destroyOnClose width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t("my_mcps.table.name")} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("my_mcps.table.url")} name="url" rules={[{ required: true }]}>
            <Input placeholder="https://mcp.example.com/sse" />
          </Form.Item>
          <Form.Item
            label={t("my_mcps.form.headers_json")}
            name="headers_json"
            rules={[{
              validator: (_, value) => {
                if (!value || !value.trim()) return Promise.resolve();
                try { JSON.parse(value); return Promise.resolve(); }
                catch { return Promise.reject(new Error(t("my_mcps.form.headers_json_invalid"))); }
              },
            }]}
          >
            <Input.TextArea
              rows={3}
              placeholder='{"Authorization": "Bearer xxx", "X-Custom": "value"}'
            />
          </Form.Item>
          <Form.Item label={t("my_mcps.form.allowed_tools")} name="allowed_tools">
            <Input placeholder={t("my_mcps.form.allowed_tools_hint")} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
